import { useEffect, useState, useRef } from 'react'
import { useLocation } from 'wouter'
import { useTranslation } from 'react-i18next'
import TetrisGame from '../games/tetris/Tetris'
import SnakeGame from '../games/snake/Snake'

interface GameDetailProps {
  gameId: string
}

const games = {
  snake: {
    title: 'Snake Game',
    component: SnakeGame
  },
  tetris: {
    title: 'Tetris',
    component: TetrisGame
  }
}

export function GameDetailPage({ gameId }: GameDetailProps) {
  const { t } = useTranslation()
  const [, setLocation] = useLocation()
  const [isLoading, setIsLoading] = useState(true)
  const [isGameMounted, setIsGameMounted] = useState(false)
  const gameContainerRef = useRef<HTMLDivElement>(null)

  const game = games[gameId as keyof typeof games]

  useEffect(() => {
    if (!game) {
      setLocation('/game')
      return
    }

    // Short timeout to ensure smooth transition
    const timer = setTimeout(() => {
      setIsLoading(false)
      setIsGameMounted(true)
      // Focus the game container after loading
      if (gameContainerRef.current) {
        gameContainerRef.current.focus()
      }
    }, 100)

    // Prevent default behavior for game control keys
    const preventDefaultForGameKeys = (e: globalThis.KeyboardEvent) => {
      const gameKeys = [
        'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
        ' ', 'Space', 'Enter', 'Escape',
        'w', 'a', 's', 'd',
        'W', 'A', 'S', 'D'
      ]
      if (gameKeys.includes(e.key)) {
        e.preventDefault()
      }
    }

    // Add the event listener to the game container
    const currentRef = gameContainerRef.current
    if (currentRef) {
      currentRef.addEventListener('keydown', preventDefaultForGameKeys)
      // Ensure the container has focus to capture keyboard events
      currentRef.focus()
    }

    return () => {
      clearTimeout(timer)
      setIsGameMounted(false)
      if (currentRef) {
        currentRef.removeEventListener('keydown', preventDefaultForGameKeys)
      }
    }
  }, [game, setLocation])

  // Ensure focus is maintained on the game container
  const handleContainerClick = () => {
    if (gameContainerRef.current) {
      gameContainerRef.current.focus()
    }
  }

  if (!game) {
    return null
  }

  const GameComponent = game.component

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">{game.title}</h1>
        <button
          onClick={() => setLocation('/game')}
          className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition-colors"
        >
          {t('games.back')}
        </button>
      </div>
      <div 
        ref={gameContainerRef}
        className="w-full min-h-[600px] rounded-lg overflow-hidden relative flex items-start justify-center focus:outline-none"
        tabIndex={0}
        onClick={handleContainerClick}
        onFocus={handleContainerClick}
      >
        {isLoading ? (
          <div className="text-gray-500">Loading game...</div>
        ) : isGameMounted && GameComponent ? (
          <div className="flex items-start justify-center">
            <GameComponent />
          </div>
        ) : (
          <div className="text-gray-500">Game not implemented yet</div>
        )}
      </div>
    </div>
  )
} 